import { Badge, Tooltip } from '@fluentui/react-components';
import { PlugDisconnected16Regular } from '@fluentui/react-icons';
import { useRealtimeStatus } from '../../hooks/useSupervisionSignalR';

/**
 * The visible half of "non-fatal must never mean invisible".
 *
 * <p>SignalR was dead in production from the day it shipped (2026-04-17) until 2026-08-06 and the
 * UI looked <em>identical</em> to a healthy one — the only trace was a `console.warn` marked
 * `(non-fatal)`. Reviewers had no way to know their board had stopped updating, so two people on
 * one review silently saw different state. This badge is the user-facing signal that the channel
 * is down; `POST realtime-diagnostics` is the queryable server-side half.</p>
 *
 * <p><b>Deliberately silent unless it matters.</b> Renders NOTHING for `connected` — a permanent
 * green "live" pip is decoration that people stop seeing, and it would make the offline state
 * harder to notice, not easier. `connecting` also renders nothing: a badge that flashes on every
 * page load teaches users to ignore it. Only `reconnecting` and `offline` are shown, because only
 * those mean the data in front of you may be stale.</p>
 *
 * <p><b>What it does NOT claim.</b> It reports the browser's own connection state, not whether the
 * server is broadcasting. A server that never emits an event looks identical to a healthy quiet
 * period from here — that gap is the server-side signal's job, and it is tracked separately.</p>
 */
export function LiveUpdatesBadge() {
  const status = useRealtimeStatus();

  if (status === 'connected' || status === 'connecting') return null;

  const isOffline = status === 'offline';

  const label = isOffline ? 'Live updates offline' : 'Reconnecting…';
  const tooltip = isOffline
    ? 'This page is not receiving live updates. Changes made by other people will not appear until you refresh.'
    : 'The live-update connection dropped and is being re-established. Changes may be delayed.';

  return (
    <Tooltip content={tooltip} relationship="description">
      <Badge
        appearance="filled"
        color={isOffline ? 'danger' : 'warning'}
        size="medium"
        icon={isOffline ? <PlugDisconnected16Regular /> : undefined}
        // The tooltip carries the explanation, but a screen reader must not have to hover to
        // learn the state — role=status announces it when it appears.
        role="status"
        aria-live="polite"
        aria-label={label}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}

export default LiveUpdatesBadge;
